import { useState, useCallback, useMemo } from 'react';
import { Session } from '@/types/session';

export function useSessionSelection(filteredSessions: Session[]) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const toggle = useCallback((sessionId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(sessionId)) {
        next.delete(sessionId);
      } else {
        next.add(sessionId);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(filteredSessions.map((s) => s.sessionId)));
  }, [filteredSessions]);

  const clearAll = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const allSelected = useMemo(
    () =>
      filteredSessions.length > 0 &&
      filteredSessions.every((s) => selectedIds.has(s.sessionId)),
    [filteredSessions, selectedIds],
  );

  const someSelected = useMemo(
    () =>
      filteredSessions.some((s) => selectedIds.has(s.sessionId)) &&
      !allSelected,
    [filteredSessions, selectedIds, allSelected],
  );

  const selectedSessionIds = useMemo(
    () =>
      filteredSessions
        .filter((s) => selectedIds.has(s.sessionId))
        .map((s) => s.sessionId),
    [filteredSessions, selectedIds],
  );

  const selectedCount = selectedSessionIds.length;

  return {
    selectedIds,
    selectedCount,
    allSelected,
    someSelected,
    selectedSessionIds,
    toggle,
    selectAll,
    clearAll,
  };
}
